import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { useClickOutside } from 'hooks/click-outside';
import { useMobileSize } from 'hooks/mobile-size';
import { Tab as BootstrapTab, Tabs as BootstrapTabs } from 'react-bootstrap';

import { Select } from 'components/FormComponents';

import styles from './Tabs.module.scss';

// Create TabContext
const TabContext = createContext();

const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) throw new Error('Tab components must be used within a TabContext');
  return context;
};

export const TabSelect = ({ children, className, defaultActiveKey, onSelect, newTabKey }) => {
  const { mobile } = useMobileSize();
  const [currentTab, setCurrentTab] = useState(defaultActiveKey);

  const setTab = key => {
    setCurrentTab(key);
    if (onSelect) onSelect(key);
  };

  useEffect(() => {
    if (newTabKey) {
      setCurrentTab(newTabKey);
    }
  }, [newTabKey]);

  const renderSelect = () => (
    <Select
      value={currentTab}
      inputProps={{ onChange: e => setTab(e.target.value) }}
      className={cn(styles.selectDropdown)}>
      {React.Children.toArray(children)
        .filter(React.isValidElement)
        .map(child => (
          <Select.Item key={child.props.eventKey} value={child.props.eventKey}>
            {child.props.title}
          </Select.Item>
        ))}
    </Select>
  );

  const renderTabs = () => (
    <BootstrapTabs
      id="accountTabs"
      onSelect={setTab}
      activeKey={currentTab}
      defaultActiveKey={defaultActiveKey}
      className={className}
      mountOnEnter
      unmountOnExit>
      {React.Children.toArray(children)
        .filter(React.isValidElement)
        .map(child => {
          const title = child.props.icon ? (
            <span className="flex items-center gap-2">
              <HugeiconsIcon icon={child.props.icon} size="16" strokeWidth={2} />
              {child.props.title}
            </span>
          ) : (
            <span className="flex items-center gap-2">{child.props.title}</span>
          );
          return React.cloneElement(child, {
            eventKey: child.props.eventKey,
            title: title,
            disabled: child.props.disabled,
          });
        })}
    </BootstrapTabs>
  );

  return (
    <TabContext.Provider value={{ currentTab, setTab }}>
      {mobile ? renderSelect() : renderTabs()}
      <div className={cn(styles.dropdownTabContent)}>
        {mobile &&
          React.Children.toArray(children)
            .filter(React.isValidElement)
            .find(child => child.props.eventKey === currentTab)?.props.children}
      </div>
    </TabContext.Provider>
  );
};

export const TabItem = ({ eventKey, title, children }) => {
  const { currentTab } = useTabContext();
  return currentTab === eventKey ? <div>{children}</div> : null;
};

TabSelect.Tab = BootstrapTab;

export const OverflowTabs = ({
  children,
  activeKey,
  defaultActiveKey,
  onSelect,
  className,
  id,
  mountOnEnter,
  unmountOnExit,
  actions,
}) => {
  const validChildren = React.Children.toArray(children).filter(React.isValidElement);
  const initialTab = activeKey || defaultActiveKey || validChildren[0]?.props.eventKey;

  const [currentTab, setCurrentTab] = useState(initialTab);
  const [visibleCount, setVisibleCount] = useState(Infinity); // Infinity = all visible
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);

  useClickOutside(moreRef, () => setDropdownOpen(false));

  // Sync controlled activeKey
  useEffect(() => {
    if (activeKey !== undefined) setCurrentTab(activeKey);
  }, [activeKey]);

  const handleSelect = key => {
    setCurrentTab(key);
    setDropdownOpen(false);
    if (onSelect) onSelect(key);
  };

  // Recalculate visible tabs whenever the container resizes or child count changes
  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const calculate = () => {
      const containerWidth = container.offsetWidth;
      const items = Array.from(measure.children);
      if (!items.length) return;

      const MORE_WIDTH = 100; // reserved width for the "More ▾" button
      const PILL_PADDING = 8; // 4px padding on each side inside the pill container

      let total = PILL_PADDING;
      let count = 0;

      for (let i = 0; i < items.length; i++) {
        const itemWidth = items[i].offsetWidth;
        const isLast = i === items.length - 1;

        if (isLast) {
          // Last item — fits without a "More" button needed
          if (total + itemWidth + PILL_PADDING <= containerWidth) count = items.length;
        } else {
          // Non-last — must reserve room for the "More" button
          if (total + itemWidth + MORE_WIDTH + PILL_PADDING <= containerWidth) {
            total += itemWidth;
            count = i + 1;
          } else {
            break;
          }
        }
      }

      setVisibleCount(count >= items.length ? Infinity : Math.max(count, 1));
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [validChildren.length]);

  const allVisible = visibleCount === Infinity;
  const visibleTabs = allVisible ? validChildren : validChildren.slice(0, visibleCount);
  const overflowTabs = allVisible ? [] : validChildren.slice(visibleCount);
  const activeOverflowTab = overflowTabs.find(t => t.props.eventKey === currentTab);

  return (
    <BootstrapTab.Container id={id} activeKey={currentTab} onSelect={handleSelect}>
      {/*
       * Hidden measurement row — fixed far off-screen so it takes no space.
       * Shares pill-tabs CSS via className so measured widths match reality.
       */}
      <ul
        ref={measureRef}
        className={cn('nav tabs pill-tabs', styles.measureRow)}
        aria-hidden="true">
        {validChildren.map(child => (
          <li key={child.props.eventKey} className="nav-item">
            <button className="nav-link" tabIndex={-1}>
              {child.props.title}
            </button>
          </li>
        ))}
      </ul>

      {/* Wrapper watched by ResizeObserver */}
      <div className={cn(className, 'flex items-center gap-3')}>
        <div ref={containerRef} className="flex-1 min-w-0">
          <ul className="nav tabs pill-tabs overflow-pill-tabs" role="tablist">
            {visibleTabs.map(child => (
              <li key={child.props.eventKey} className="nav-item" role="presentation">
                <button
                  role="tab"
                  aria-selected={currentTab === child.props.eventKey}
                  className={cn('nav-link', { active: currentTab === child.props.eventKey })}
                  onClick={() => handleSelect(child.props.eventKey)}
                  disabled={child.props.disabled}>
                  {child.props.title}
                </button>
              </li>
            ))}

            {overflowTabs.length > 0 && (
              <li
                ref={moreRef}
                className="nav-item relative flex-shrink-0 whitespace-nowrap"
                role="presentation">
                <button
                  className={cn('nav-link whitespace-nowrap', { active: !!activeOverflowTab })}
                  onClick={() => setDropdownOpen(o => !o)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true">
                  <span className="flex items-center gap-1">
                    {activeOverflowTab ? activeOverflowTab.props.title : 'More'}
                    <HugeiconsIcon
                      icon={ArrowDown01Icon}
                      size="16"
                      strokeWidth={2}
                      className={cn('flex-shrink-0 transition-transform duration-200', {
                        'rotate-180': dropdownOpen,
                      })}
                    />
                  </span>
                </button>

                {dropdownOpen && (
                  <div
                    className={cn(
                      'absolute right-0 top-full z-[1000] mt-1.5 min-w-[180px] overflow-hidden rounded-xl p-1',
                      styles.overflowMenu,
                    )}>
                    {overflowTabs.map(child => (
                      <button
                        key={child.props.eventKey}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-6 py-2 text-left text-sm whitespace-nowrap',
                          styles.overflowMenuItem,
                          { [styles.overflowMenuItemActive]: currentTab === child.props.eventKey },
                        )}
                        onClick={() => handleSelect(child.props.eventKey)}
                        disabled={child.props.disabled}>
                        {child.props.title}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            )}
          </ul>
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>

      <BootstrapTab.Content>
        {validChildren.map(child => (
          <BootstrapTab.Pane
            key={child.props.eventKey}
            eventKey={child.props.eventKey}
            mountOnEnter={mountOnEnter}
            unmountOnExit={unmountOnExit}>
            {child.props.children}
          </BootstrapTab.Pane>
        ))}
      </BootstrapTab.Content>
    </BootstrapTab.Container>
  );
};

OverflowTabs.Tab = BootstrapTab;
